// src/components/Quotation/QuotationGenerateFromPOModal.jsx
import React, { useState, useEffect } from "react";
import { generateFromPO, fetchPurchaseOrders } from "../../api/quotationApi";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const QuotationGenerateFromPOModal = ({ onClose, onSuccess }) => {
  const [poList, setPoList]         = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedPoId, setSelectedPoId] = useState("");
  const [manualId, setManualId]     = useState("");
  const [isManual, setIsManual]     = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");

  useEffect(() => {
    const loadPOs = async () => {
      setLoadingList(true);
      try {
        const adminId = localStorage.getItem("adminId") || localStorage.getItem("useradminid");
        const list = await fetchPurchaseOrders(adminId);
        if (Array.isArray(list)) {
          setPoList(list);
          if (list.length > 0) {
            setSelectedPoId(list[0].id || list[0].poid || "");
          }
        }
      } catch (err) {
        console.warn("Could not fetch PO list, allowing manual input:", err);
        setIsManual(true);
      } finally {
        setLoadingList(false);
      }
    };
    loadPOs();
  }, []);

  const handleSubmit = async () => {
    const targetId = isManual ? manualId.trim() : selectedPoId;
    if (!targetId) return setError("Please select or enter a Purchase Order.");
    if (!UUID_REGEX.test(targetId))
      return setError("Invalid PO ID format. Expected standard UUID format.");

    setError("");
    setLoading(true);
    try {
      const res = await generateFromPO(targetId);
      if (res.ok) {
        const data = await res.json();
        onSuccess(data);
        onClose();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.message || err.error || `Failed with status ${res.status}`);
      }
    } catch (e) {
      setError("Network error while connecting to backend.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={modalHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: 18 }}>🧾</span>
            <span style={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
              Generate Quotation from Purchase Order
            </span>
          </div>
          <button onClick={onClose} style={closeBtn}>✕</button>
        </div>

        <div style={{ padding: "20px" }}>
          <p style={{ fontSize: 13, color: "#4B5563", marginBottom: 16 }}>
            Select an existing Purchase Order to instantly generate a full Quotation with items, pricing, and addresses.
          </p>

          {!isManual && poList.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <label style={label}>Select Purchase Order *</label>
              <select
                style={select}
                value={selectedPoId}
                onChange={e => setSelectedPoId(e.target.value)}
              >
                {poList.map((po) => {
                  const id = po.id || po.poid;
                  const num = po.ponumber || po.PONumber || po.subject || id?.slice(0, 8);
                  const total = po.totalPOamount || po.totalAmount || po.dueAmount || 0;
                  const date = po.duedate || po.createdAt || "";
                  return (
                    <option key={id} value={id}>
                      {num} — ₹{Number(total).toLocaleString("en-IN")} {date ? `(${date.slice(0, 10)})` : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          ) : (
            <div style={{ marginBottom: 16 }}>
              <label style={label}>Purchase Order ID (UUID) *</label>
              <input
                style={input}
                placeholder="e.g. 255fb421-03ef-4959-b098-63af37161035"
                value={manualId}
                onChange={e => setManualId(e.target.value)}
              />
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <button
              type="button"
              onClick={() => setIsManual(!isManual)}
              style={{ background: "none", border: "none", color: "#6366F1", fontSize: 12.5, cursor: "pointer", padding: 0, textDecoration: "underline" }}
            >
              {isManual ? "← Select from available POs" : "Enter PO UUID manually"}
            </button>
          </div>

          {error && <div style={errorBox}>{error}</div>}
        </div>

        <div style={modalFooter}>
          <button onClick={onClose} style={cancelBtn} disabled={loading}>Cancel</button>
          <button onClick={handleSubmit} disabled={loading || loadingList} style={submitBtn}>
            {loading ? "Generating..." : "Generate Quotation"}
          </button>
        </div>
      </div>
    </div>
  );
};

// styles
const overlay  = { position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:10000,backdropFilter:"blur(2px)" };
const modal    = { background:"#fff",borderRadius:12,width:"90%",maxWidth:520,boxShadow:"0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" };
const modalHeader = { display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 20px",borderBottom:"1px solid #E5E7EB" };
const closeBtn = { background:"none",border:"none",cursor:"pointer",fontSize:18,color:"#6B7280" };
const modalFooter = { display:"flex",justifyContent:"flex-end",gap:10,padding:"14px 20px",borderTop:"1px solid #E5E7EB",background:"#F9FAFB",borderRadius:"0 0 12px 12px" };
const label    = { display:"block",fontSize:13,fontWeight:600,color:"#374151",marginBottom:6 };
const select   = { width:"100%",padding:"10px 12px",borderRadius:8,border:"1px solid #D1D5DB",fontSize:13.5,outline:"none",boxSizing:"border-box",background:"#fff" };
const input    = { width:"100%",padding:"10px 12px",borderRadius:8,border:"1px solid #D1D5DB",fontSize:13.5,outline:"none",boxSizing:"border-box" };
const errorBox = { fontSize:12.5,color:"#DC2626",background:"#FEF2F2",border:"1px solid #FCA5A5",borderRadius:8,padding:"10px 12px",marginTop:12 };
const cancelBtn = { fontSize:13,fontWeight:500,padding:"8px 16px",borderRadius:8,background:"#fff",border:"1px solid #D1D5DB",color:"#374151",cursor:"pointer" };
const submitBtn = { fontSize:13,fontWeight:600,padding:"8px 20px",borderRadius:8,background:"#6366F1",color:"#fff",border:"none",cursor:"pointer" };

export default QuotationGenerateFromPOModal;

